import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

import {
  applyPlacement,
  clampLayout,
  readingOrder,
  type LayoutItem,
  type WidgetLayout,
} from "../lib/dashboardLayout";

// Dashboard-local 12-column widget grid for the UI-LAW §4c Board archetype.
// View mode renders explicitly placed tiles; edit mode adds a per-tile Move
// handle (pointer drag + arrow-key move, Shift+arrows resize) and a SE resize
// handle. Every layout mutation goes through the pure `dashboardLayout` engine
// (push-down + vertical compaction) and is reported upward via `onLayoutChange`.
// Narrow viewports (<64rem) stack tiles in reading order (y, then x).
//
// Law notes: placement is set via element style properties from a layout
// effect (this file is the styling owner, src/ui); tiles snap cell-to-cell
// with no transforms or transitions (§2.9 — the only drag feedback is the
// sanctioned opacity dim on the lifted tile).

export interface DashboardWidgetGridProps {
  items: LayoutItem[];
  renderTile: (id: string) => ReactNode;
  editable?: boolean;
  /** Fired with the full new layout set after a drop / keyboard move. */
  onLayoutChange?: (items: LayoutItem[]) => void;
  tileLabels?: Record<string, string>;
}

interface DragState {
  id: string;
  mode: "move" | "resize";
  pointerId: number;
  startX: number;
  startY: number;
  startLayout: WidgetLayout;
  items: LayoutItem[];
}

function cellMetrics(container: HTMLElement): {
  columnWidth: number;
  rowHeight: number;
} {
  const style = window.getComputedStyle(container);
  const gap = Number.parseFloat(style.columnGap) || 0;
  const rowHeight = Number.parseFloat(style.gridAutoRows) || 72;
  const width = container.getBoundingClientRect().width;
  const columnWidth = (width - gap * 11) / 12 + gap;
  return { columnWidth, rowHeight: rowHeight + gap };
}

function useIsWideViewport(): boolean {
  const [isWide, setIsWide] = useState(
    () => window.matchMedia("(min-width: 64rem)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(min-width: 64rem)");
    const onChange = () => {
      setIsWide(media.matches);
    };
    media.addEventListener("change", onChange);
    return () => {
      media.removeEventListener("change", onChange);
    };
  }, []);
  return isWide;
}

function GridTile({
  children,
  isDragging,
  layout,
  placed,
}: {
  children: ReactNode;
  isDragging: boolean;
  layout: WidgetLayout;
  placed: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    if (placed) {
      element.style.gridColumn = `${String(layout.x + 1)} / span ${String(layout.width)}`;
      element.style.gridRow = `${String(layout.y + 1)} / span ${String(layout.height)}`;
    } else {
      element.style.removeProperty("grid-column");
      element.style.removeProperty("grid-row");
    }
  }, [layout.height, layout.width, layout.x, layout.y, placed]);
  return (
    <div
      className={
        isDragging
          ? "marker-dashboard-grid__tile marker-dashboard-grid__tile--dragging"
          : "marker-dashboard-grid__tile"
      }
      ref={ref}
    >
      {children}
    </div>
  );
}

export function DashboardWidgetGrid({
  editable = false,
  items,
  renderTile,
  onLayoutChange,
  tileLabels,
}: DashboardWidgetGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const isWide = useIsWideViewport();

  const displayed = drag?.items ?? items;
  const ordered = readingOrder(displayed);
  const placed = isWide;

  const beginDrag = useCallback(
    (
      event: ReactPointerEvent<HTMLButtonElement>,
      id: string,
      mode: "move" | "resize",
    ) => {
      const layout = items.find((item) => item.id === id)?.layout;
      if (!layout || !editable || !placed) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({
        id,
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLayout: layout,
        items,
      });
    },
    [editable, items, placed],
  );

  const updateDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const state = dragRef.current;
      const container = containerRef.current;
      if (!state || !container || event.pointerId !== state.pointerId) {
        return;
      }
      const { columnWidth, rowHeight } = cellMetrics(container);
      const deltaColumns = Math.round(
        (event.clientX - state.startX) / columnWidth,
      );
      const deltaRows = Math.round((event.clientY - state.startY) / rowHeight);
      const target =
        state.mode === "move"
          ? {
              ...state.startLayout,
              x: state.startLayout.x + deltaColumns,
              y: state.startLayout.y + deltaRows,
            }
          : {
              ...state.startLayout,
              width: state.startLayout.width + deltaColumns,
              height: state.startLayout.height + deltaRows,
            };
      const next = applyPlacement(items, state.id, clampLayout(target));
      setDrag({ ...state, items: next });
    },
    [items],
  );

  const endDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const state = dragRef.current;
      if (!state || event.pointerId !== state.pointerId) {
        return;
      }
      setDrag(null);
      onLayoutChange?.(state.items);
    },
    [onLayoutChange],
  );

  // Arrow keys move one cell; Shift+arrows resize one cell. Each keypress
  // commits (no drag session), so the keyboard path needs no extra Save step.
  const onHandleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>, id: string) => {
      const layout = items.find((item) => item.id === id)?.layout;
      if (!layout) {
        return;
      }
      const step = { x: 0, y: 0 };
      if (event.key === "ArrowLeft") {
        step.x = -1;
      } else if (event.key === "ArrowRight") {
        step.x = 1;
      } else if (event.key === "ArrowUp") {
        step.y = -1;
      } else if (event.key === "ArrowDown") {
        step.y = 1;
      } else {
        return;
      }
      event.preventDefault();
      const target = event.shiftKey
        ? {
            ...layout,
            width: layout.width + step.x,
            height: layout.height + step.y,
          }
        : { ...layout, x: layout.x + step.x, y: layout.y + step.y };
      onLayoutChange?.(applyPlacement(items, id, clampLayout(target)));
    },
    [items, onLayoutChange],
  );

  return (
    <div
      className={
        editable
          ? "marker-dashboard-grid marker-dashboard-grid--editable"
          : "marker-dashboard-grid"
      }
      ref={containerRef}
    >
      {ordered.map((item) => (
        <GridTile
          key={item.id}
          isDragging={drag?.id === item.id}
          layout={item.layout}
          placed={placed}
        >
          {editable && placed ? (
            <div className="marker-dashboard-grid__edit-chrome">
              <button
                aria-label={`Move ${tileLabels?.[item.id] ?? "widget"} — arrow keys move, Shift+arrows resize`}
                className="marker-dashboard-grid__handle"
                type="button"
                onKeyDown={(event) => {
                  onHandleKeyDown(event, item.id);
                }}
                onPointerDown={(event) => {
                  beginDrag(event, item.id, "move");
                }}
                onPointerMove={updateDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                Move
              </button>
              <button
                aria-label={`Resize ${tileLabels?.[item.id] ?? "widget"}`}
                className="marker-dashboard-grid__resize"
                type="button"
                onPointerDown={(event) => {
                  beginDrag(event, item.id, "resize");
                }}
                onPointerMove={updateDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              />
            </div>
          ) : null}
          {renderTile(item.id)}
        </GridTile>
      ))}
    </div>
  );
}

export type { LayoutItem, WidgetLayout };
